import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { UserProfile, DailyRecord, Reward } from '../types';

interface SparrowDB extends DBSchema {
  users: {
    key: string;
    value: UserProfile;
    indexes: { 'by-email': string };
  };
  dailyRecords: {
    key: string;
    value: DailyRecord;
    indexes: { 'by-user': string; 'by-date': Date };
  };
  rewards: {
    key: string;
    value: Reward;
    indexes: { 'by-user': string; 'by-status': string };
  };
}

const dbName = 'sparrow-db';
const version = 1;

async function initDB(): Promise<IDBPDatabase<SparrowDB>> {
  return openDB<SparrowDB>(dbName, version, {
    upgrade(db) {
      // 사용자 저장소
      if (!db.objectStoreNames.contains('users')) {
        const userStore = db.createObjectStore('users', { keyPath: 'id' });
        userStore.createIndex('by-email', 'email', { unique: true });
      }

      // 일일 기록 저장소
      if (!db.objectStoreNames.contains('dailyRecords')) {
        const recordStore = db.createObjectStore('dailyRecords', { keyPath: 'id' });
        recordStore.createIndex('by-user', 'userId', { unique: false });
        recordStore.createIndex('by-date', 'date', { unique: false });
      }

      // 보상 저장소
      if (!db.objectStoreNames.contains('rewards')) {
        const rewardStore = db.createObjectStore('rewards', { keyPath: 'id' });
        rewardStore.createIndex('by-user', 'userId', { unique: false });
        rewardStore.createIndex('by-status', 'status', { unique: false });
      }
    }
  });
}

export const userService = {
  async createUser(userData: Omit<UserProfile, 'id'>): Promise<string> {
    try {
      const db = await initDB();
      const id = crypto.randomUUID();
      
      // 필수 필드 검증
      if (!userData.name || !userData.email) {
        throw new Error('필수 사용자 정보가 누락되었습니다.');
      }

      const newUser: UserProfile = {
        id,
        name: userData.name,
        email: userData.email,
        stats: {
          totalMinutesCompleted: userData.stats?.totalMinutesCompleted ?? 0,
          streakCount: userData.stats?.streakCount ?? 0,
          lastActiveDate: userData.stats?.lastActiveDate ?? new Date(),
          achievements: userData.stats?.achievements ?? [],
          level: userData.stats?.level ?? 1,
          experience: userData.stats?.experience ?? 0,
          donationAmount: userData.stats?.donationAmount ?? 0,
          donationCount: userData.stats?.donationCount ?? 0
        },
        settings: {
          theme: userData.settings?.theme ?? 'light',
          soundEnabled: userData.settings?.soundEnabled ?? true,
          notificationsEnabled: userData.settings?.notificationsEnabled ?? true,
          language: userData.settings?.language ?? 'ko'
        },
        createdAt: new Date(),
        totalMinutesCompleted: 0,
        streakCount: 0,
        mentalActivity: undefined,
        physicalActivity: undefined
      };
      
      await db.add('users', newUser);
      return id;
    } catch (error) {
      console.error('사용자 생성 실패:', error);
      throw error;
    }
  },

  async getUser(id: string): Promise<UserProfile | null> {
    try {
      const db = await initDB();
      const user = await db.get('users', id);
      if (!user) return null;
      
      // 타입 안전성을 위한 검증
      if (!user.id || !user.name || !user.email || !user.stats || !user.settings) {
        console.error('잘못된 사용자 데이터:', user);
        return null;
      }

      return {
        ...user,
        stats: {
          totalMinutesCompleted: user.stats.totalMinutesCompleted || 0,
          streakCount: user.stats.streakCount || 0,
          lastActiveDate: user.stats.lastActiveDate || new Date(),
          achievements: user.stats.achievements || [],
          level: user.stats.level || 1,
          experience: user.stats.experience || 0,
          donationAmount: user.stats.donationAmount || 0,
          donationCount: user.stats.donationCount || 0
        },
        settings: {
          theme: user.settings.theme || 'light',
          soundEnabled: user.settings.soundEnabled ?? true,
          notificationsEnabled: user.settings.notificationsEnabled ?? true,
          language: user.settings.language || 'ko'
        },
        createdAt: user.createdAt || new Date()
      };
    } catch (error) {
      console.error('사용자 조회 실패:', error);
      return null;
    }
  },

  updateUser: async (userId: string, userData: Partial<UserProfile>) => {
    try {
      const db = await initDB();
      const user = await userService.getUser(userId);
      if (!user) throw new Error('사용자를 찾을 수 없습니다.');

      const updatedUser: UserProfile = {
        ...user,
        ...userData,
        stats: {
          ...user.stats,
          ...userData.stats,
          lastActiveDate: new Date()
        },
        settings: {
          ...user.settings,
          ...userData.settings
        }
      };

      await db.put('users', updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('사용자 업데이트 실패:', error);
      throw error;
    }
  },
};

export const dailyRecordService = {
  async getDailyRecord(userId: string, date: Date): Promise<DailyRecord | null> {
    try {
      const db = await initDB();
      const records = await db.getAllFromIndex('dailyRecords', 'by-user', userId);
      const record = records.find(r => r.date.toDateString() === date.toDateString());
      
      if (!record) {
        // 새로운 일일 기록 생성
        const newRecord: Omit<DailyRecord, 'id'> = {
          userId,
          date,
          physicalMinutes: 0,
          mentalMinutes: 0,
          totalMinutes: 0,
          completedActivities: [],
          notes: '',
          mood: 0,
          physicalCompleted: false,
          mentalCompleted: false,
          completed: false,
          isFullyCompleted: false
        };
        
        const id = await this.createDailyRecord(newRecord);
        return { ...newRecord, id };
      }
      
      return record;
    } catch (error) {
      console.error('일일 기록 조회 실패:', error);
      return null;
    }
  },

  async getUserRecords(userId: string): Promise<DailyRecord[]> {
    try {
      const db = await initDB();
      const records = await db.getAllFromIndex('dailyRecords', 'by-user', userId);
      return records.sort((a, b) => a.date.getTime() - b.date.getTime());
    } catch (error) {
      console.error('사용자 기록 조회 실패:', error);
      return [];
    }
  },

  async createDailyRecord(record: Omit<DailyRecord, 'id'>): Promise<string> {
    try {
      const db = await initDB();
      const id = crypto.randomUUID();
      await db.add('dailyRecords', { ...record, id });
      return id;
    } catch (error) {
      console.error('일일 기록 생성 실패:', error);
      throw error;
    }
  },

  async updateDailyRecord(_id: string, record: DailyRecord): Promise<void> {
    try {
      const db = await initDB();
      await db.put('dailyRecords', record);
    } catch (error) {
      console.error('일일 기록 업데이트 실패:', error);
      throw error;
    }
  }
};

export const rewardService = {
  async getRewards(userId: string): Promise<Reward[]> {
    try {
      const db = await initDB();
      const rewards = await db.getAllFromIndex('rewards', 'by-user', userId);
      return rewards.map(reward => ({
        ...reward,
        earnedDate: reward.earnedDate || new Date(),
        claimedDate: reward.claimedDate || new Date()
      }));
    } catch (error) {
      console.error('보상 조회 실패:', error);
      return [];
    }
  },

  async createReward(reward: Omit<Reward, 'id'>): Promise<string> {
    try {
      const db = await initDB();
      const id = crypto.randomUUID();
      const newReward: Reward = {
        ...reward,
        id,
        earnedDate: reward.earnedDate || new Date(),
        claimedDate: reward.claimedDate || new Date()
      };
      await db.add('rewards', newReward);
      return id;
    } catch (error) {
      console.error('보상 생성 실패:', error);
      throw error;
    }
  },

  async updateReward(_id: string, reward: Reward): Promise<void> {
    try {
      const db = await initDB();
      const updatedReward: Reward = {
        ...reward,
        earnedDate: reward.earnedDate || new Date(),
        claimedDate: reward.claimedDate || new Date()
      };
      await db.put('rewards', updatedReward);
    } catch (error) {
      console.error('보상 업데이트 실패:', error);
      throw error;
    }
  }
}; 